import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ShoppingCart, Package, FileText, BarChart3, Truck, Shield,
  ChevronDown, Menu, X,
  Phone, Mail, MapPin, LogIn, UserPlus, Check, Lock, User,
  Monitor, Search, ClipboardList, Send, ArrowRight
} from 'lucide-react';
import './HomePage.css';

/* ─── Images ─── */
const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1600&q=80',
  pedidos: 'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=600&q=80',
  catalogo: 'https://images.unsplash.com/photo-1583947215259-38e31be8751f?w=600&q=80',
  facturacion: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&q=80',
  estadisticas: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80',
  logistica: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&q=80',
  seguridad: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&q=80',
  faq: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&q=80',
};

/* ─── Intersection Observer Hook ─── */
const useIntersectionObserver = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    const elements = document.querySelectorAll('.observe-me');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
};

/* ─── Animated Counter Hook ─── */
const useAnimatedCounter = (target, duration = 1800) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const start = performance.now();

          const animate = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(animate);
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [target, duration]);

  return [ref, count];
};

/* ─── Stat Card Component ─── */
const StatCard = ({ value, suffix, prefix, label }) => {
  const numericValue = parseInt(value.replace(/\D/g, ''), 10);
  const isNumeric = !isNaN(numericValue) && numericValue > 0;
  const [ref, count] = useAnimatedCounter(isNumeric ? numericValue : 0);

  return (
    <div ref={ref} className="stat-card">
      <div className="stat-value text-3xl md:text-4xl font-bold text-white mb-2">
        {isNumeric ? (
          <>
            {prefix && <span>{prefix}</span>}
            {count.toLocaleString('es-ES')}
            {suffix && <span>{suffix}</span>}
          </>
        ) : (
          value
        )}
      </div>
      <div className="text-blue-200 text-sm font-medium">{label}</div>
    </div>
  );
};

/* ─── Main Component ─── */
const HomePage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  /* Navbar */
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);

  /* Process steps */
  const [activeStep, setActiveStep] = useState(0);

  /* Login form */
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  /* Request access form */
  const [requestForm, setRequestForm] = useState({
    centro: '', contacto: '', email: '', telefono: '', mensaje: ''
  });
  const [requestSent, setRequestSent] = useState(false);
  const [requestErrors, setRequestErrors] = useState({});

  /* FAQ */
  const [openFaq, setOpenFaq] = useState(null);

  /* Init observer */
  useIntersectionObserver();

  /* Navbar scroll effect */
  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Auto-cycle process steps */
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  /* Smooth scroll */
  const scrollTo = useCallback((id) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  /* Login handler */
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const data = await login(loginUsername, loginPassword);
      const permisos = data.user?.permisos;
      if (permisos === 1 || permisos === 2 || permisos === 6) {
        navigate('/gestion', { replace: true });
      } else if (permisos === 3) {
        navigate('/comercial', { replace: true });
      } else if (permisos === 5) {
        navigate('/cliente', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      setLoginError(err.message || 'Credenciales incorrectas. Verifique usuario y contraseña.');
    } finally {
      setLoginLoading(false);
    }
  };

  /* Request access handler */
  const handleRequestAccess = (e) => {
    e.preventDefault();
    const errors = {};
    if (!requestForm.centro.trim()) errors.centro = 'Campo obligatorio';
    if (!requestForm.contacto.trim()) errors.contacto = 'Campo obligatorio';
    if (!requestForm.email.trim()) errors.email = 'Campo obligatorio';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(requestForm.email)) errors.email = 'Email no válido';
    if (!requestForm.telefono.trim()) errors.telefono = 'Campo obligatorio';

    if (Object.keys(errors).length > 0) {
      setRequestErrors(errors);
      return;
    }

    setRequestErrors({});
    setRequestSent(true);
  };

  /* Data */
  const navLinks = [
    { label: 'Inicio', target: 'hero' },
    { label: 'Cómo Funciona', target: 'proceso' },
    { label: 'Servicios', target: 'servicios' },
    { label: 'Acceso', target: 'login' },
    { label: 'FAQ', target: 'faq' },
  ];

  const processSteps = [
    { icon: Monitor, title: 'Acceda a la plataforma', desc: 'Inicie sesión con sus credenciales de cliente' },
    { icon: Search, title: 'Explore el catálogo', desc: 'Navegue productos por familia y referencia' },
    { icon: ClipboardList, title: 'Realice su pedido', desc: 'Añada productos y confirme su pedido' },
    { icon: Truck, title: 'Reciba en su centro', desc: 'Seguimiento de entrega y facturación digital' },
  ];

  const features = [
    { icon: ShoppingCart, title: 'Pedidos Online', desc: 'Realice pedidos 24/7 desde cualquier dispositivo. Ofertas exclusivas y disponibilidad actualizada para su centro.', image: IMAGES.pedidos },
    { icon: Package, title: 'Catálogo Completo', desc: 'Más de 5.000 referencias de productos sanitarios y parafarmacia de las mejores marcas.', image: IMAGES.catalogo },
    { icon: FileText, title: 'Facturación Digital', desc: 'Acceda a facturas, albaranes y documentos comerciales en formato digital.', image: IMAGES.facturacion },
    { icon: BarChart3, title: 'Estadísticas', desc: 'Análisis detallado de sus compras, ahorros y tendencias de consumo.', image: IMAGES.estadisticas },
    { icon: Truck, title: 'Logística Nacional', desc: 'Red de distribución que cubre todo el territorio nacional con entregas rápidas.', image: IMAGES.logistica },
    { icon: Shield, title: 'Gestión Segura', desc: 'Plataforma segura con autenticación y control de acceso por roles.', image: IMAGES.seguridad },
  ];

  const faqs = [
    {
      q: '¿Cómo puedo empezar a comprar en ComprasSanitarias?',
      a: 'Solicite acceso a través de nuestro formulario de registro. Nuestro equipo comercial verificará sus datos y le enviará las credenciales de acceso en un plazo de 24-48 horas laborables.'
    },
    {
      q: '¿Qué productos ofrecen?',
      a: 'Distribuimos más de 5.000 referencias de productos sanitarios y de parafarmacia, incluyendo material clínico, productos de higiene, instrumental médico y equipamiento para centros de salud de las principales marcas del sector.'
    },
    {
      q: '¿Cuáles son los plazos de entrega?',
      a: 'Las entregas se realizan en 24-48 horas laborables en península. Para pedidos realizados antes de las 14:00h, la entrega se gestiona al día siguiente. Zonas insulares y especiales pueden requerir plazos adicionales.'
    },
    {
      q: '¿Puedo ver mis facturas online?',
      a: 'Sí, todos los documentos comerciales están disponibles en su área de cliente: facturas, albaranes, notas de crédito y extractos de cuenta. Puede descargarlos en formato PDF o consultarlos directamente en pantalla.'
    },
    {
      q: '¿Ofrecen condiciones especiales por volumen?',
      a: 'Sí, cada cliente tiene tarifas ajustadas según su acuerdo comercial. Además, publicamos ofertas y promociones periódicas visibles directamente en la plataforma.'
    },
  ];

  return (
    <div className="min-h-screen bg-white">

      {/* ─── NAVBAR ─── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        navScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0">
              <img src="/logo.png" alt="ComprasSanitarias" className="h-16 w-auto" />
            </div>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <button
                  key={link.target}
                  onClick={() => scrollTo(link.target)}
                  className="nav-link bg-transparent border-none cursor-pointer"
                >
                  {link.label}
                </button>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <button
                onClick={() => scrollTo('login')}
                className="flex items-center gap-2 text-sm font-medium text-text-muted hover:text-primary transition px-4 py-2 rounded-lg hover:bg-gray-50"
              >
                <LogIn size={16} />
                Iniciar Sesión
              </button>
              <button
                onClick={() => scrollTo('demo')}
                className="flex items-center gap-2 text-sm font-semibold text-white bg-primary hover:bg-primary-dark transition px-5 py-2.5 rounded-lg"
              >
                <UserPlus size={16} />
                Solicitar Acceso
              </button>
            </div>

            {/* Mobile hamburger */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          <div className={`mobile-menu lg:hidden ${mobileMenuOpen ? 'open' : ''}`}>
            <div className="py-3 space-y-1 border-t border-gray-100">
              {navLinks.map((link) => (
                <button
                  key={link.target}
                  onClick={() => scrollTo(link.target)}
                  className="block w-full text-left px-3 py-2.5 text-sm font-medium text-text-muted hover:text-primary hover:bg-gray-50 rounded-lg transition"
                >
                  {link.label}
                </button>
              ))}
              <div className="flex gap-2 pt-2 px-3">
                <button
                  onClick={() => scrollTo('login')}
                  className="flex-1 text-center text-sm font-medium py-2.5 border border-gray-200 rounded-lg hover:border-primary hover:text-primary transition"
                >
                  Iniciar Sesión
                </button>
                <button
                  onClick={() => scrollTo('demo')}
                  className="flex-1 text-center text-sm font-semibold text-white bg-primary rounded-lg py-2.5 hover:bg-primary-dark transition"
                >
                  Solicitar Acceso
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* ─── HERO SECTION (full viewport, bg image + gradient + stats at bottom) ─── */}
      <section id="hero" className="hero-section px-4 sm:px-6">
        <img
          src={IMAGES.hero}
          alt=""
          className="hero-bg-image"
          loading="eager"
        />
        <div className="max-w-7xl mx-auto relative z-10 w-full pt-24">
          <div className="max-w-3xl mx-auto text-center lg:text-left lg:mx-0 mb-20">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white/90 px-4 py-1.5 rounded-full text-sm font-medium mb-8 border border-white/15">
              <Shield size={14} />
              Plataforma B2B para profesionales de la salud
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-[1.1] text-balance">
              Distribución sanitaria{' '}
              <span className="text-blue-200">inteligente</span>{' '}
              para su centro
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 mb-10 max-w-2xl leading-relaxed">
              Gestione pedidos, consulte su catálogo y controle su facturación desde una plataforma
              diseñada para profesionales de la salud. Más de 25 años al servicio del sector sanitario.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button
                onClick={() => scrollTo('demo')}
                className="inline-flex items-center justify-center gap-2 bg-white text-primary font-semibold px-8 py-4 rounded-xl hover:bg-blue-50 transition text-lg"
              >
                <UserPlus size={20} />
                Solicitar Acceso
              </button>
              <button
                onClick={() => scrollTo('login')}
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white font-medium px-8 py-4 rounded-xl hover:bg-white/20 transition text-lg border border-white/20"
              >
                <LogIn size={20} />
                Iniciar Sesión
              </button>
            </div>
          </div>

          {/* Stats integrated in hero */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 pb-8">
            <StatCard prefix="+" value="2000" suffix="" label="Clientes activos" />
            <StatCard prefix="+" value="5000" suffix="" label="Referencias" />
            <StatCard prefix="+" value="25" suffix="" label="Años de experiencia" />
            <StatCard value="Nacional" label="Cobertura" />
          </div>
        </div>
      </section>

      {/* ─── PROCESS SECTION (full viewport) ─── */}
      <section id="proceso" className="section-full bg-white">
        <div className="max-w-6xl mx-auto w-full">
          <div className="text-center mb-20 observe-me">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text mb-4">
              Cómo funciona
            </h2>
            <p className="text-text-muted max-w-2xl mx-auto text-lg">
              En cuatro sencillos pasos, gestione todas sus compras sanitarias de forma eficiente
            </p>
          </div>

          <div className="process-timeline">
            <div className="process-connector hidden md:block" />
            <div className="grid md:grid-cols-4 gap-8 md:gap-4">
              {processSteps.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div
                    key={i}
                    className={`process-step text-center ${activeStep === i ? 'active' : ''}`}
                    onClick={() => setActiveStep(i)}
                    style={{ transitionDelay: `${i * 80}ms` }}
                  >
                    <div className="process-step-number">
                      <Icon size={28} />
                    </div>
                    <h3 className="process-step-title">{step.title}</h3>
                    <p className="process-step-desc">{step.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FEATURES / SERVICES (full viewport, cards with images) ─── */}
      <section id="servicios" className="section-full bg-background">
        <div className="max-w-7xl mx-auto w-full">
          <div className="text-center mb-16 observe-me">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text mb-4">
              Todo lo que necesita para su gestión sanitaria
            </h2>
            <p className="text-text-muted max-w-2xl mx-auto text-lg">
              Herramientas completas para gestionar su relación comercial con Hispafarma
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
            {features.map((feat, i) => {
              const Icon = feat.icon;
              return (
                <div key={i} className="feature-card observe-me">
                  <div className="feature-card-image">
                    <img
                      src={feat.image}
                      alt={feat.title}
                      loading="lazy"
                    />
                    <div className="feature-card-icon-overlay">
                      <Icon size={24} className="text-primary" />
                    </div>
                  </div>
                  <div className="feature-card-body">
                    <h3 className="text-lg font-semibold text-text mb-2">{feat.title}</h3>
                    <p className="text-text-muted text-sm leading-relaxed">{feat.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── LOGIN SECTION (full viewport) ─── */}
      <section id="login" className="section-full login-section">
        <div className="max-w-6xl mx-auto w-full">
          <div className="text-center mb-16 observe-me">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text mb-4">Acceda a su cuenta</h2>
            <p className="text-text-muted max-w-xl mx-auto text-lg">
              Inicie sesión para gestionar pedidos, consultar precios y descargar documentos
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Visual */}
            <div className="observe-me slide-left hidden lg:block">
              <h3 className="text-2xl font-bold text-text mb-4">
                Su portal de compras sanitarias
              </h3>
              <p className="text-text-muted mb-8 leading-relaxed text-lg">
                Desde su cuenta podrá gestionar pedidos, consultar el catálogo, descargar facturas
                y albaranes, y hacer seguimiento de sus entregas con Hispana de Parafarmacia.
              </p>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=80"
                  alt="Material sanitario profesional"
                  className="w-full object-cover"
                  style={{ height: '320px' }}
                  loading="lazy"
                />
              </div>
            </div>

            {/* Right: Login Form */}
            <div className="observe-me slide-right">
              <div className="login-form-container p-8 md:p-10">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 bg-primary-light rounded-xl flex items-center justify-center">
                    <LogIn size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-text">Iniciar Sesión</h4>
                    <p className="text-xs text-text-muted">Introduzca sus credenciales</p>
                  </div>
                </div>

                <form onSubmit={handleLogin} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-text mb-1.5">Usuario</label>
                    <div className="relative">
                      <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={loginUsername}
                        onChange={(e) => setLoginUsername(e.target.value)}
                        className="login-input pl-10"
                        placeholder="Introduzca su usuario"
                        required
                        autoComplete="username"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text mb-1.5">Contraseña</label>
                    <div className="relative">
                      <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="login-input pl-10"
                        placeholder="Introduzca su contraseña"
                        required
                        autoComplete="current-password"
                      />
                    </div>
                  </div>

                  {loginError && (
                    <div className="bg-red-50 text-danger text-sm p-3 rounded-lg border border-red-100">
                      {loginError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loginLoading}
                    className="login-btn flex items-center justify-center gap-2"
                  >
                    {loginLoading ? (
                      <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <LogIn size={18} />
                        Acceder
                      </>
                    )}
                  </button>
                </form>

                <p className="text-center text-sm mt-4">
                  <a
                    href="/recuperar-password"
                    className="text-primary font-medium hover:underline"
                  >
                    ¿Olvidó su contraseña?
                  </a>
                </p>

                <p className="text-center text-sm text-text-muted mt-4">
                  ¿No tiene cuenta?{' '}
                  <button
                    onClick={() => scrollTo('demo')}
                    className="text-primary font-medium hover:underline bg-transparent border-none cursor-pointer"
                  >
                    Solicite acceso aquí
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── REQUEST ACCESS SECTION (full viewport) ─── */}
      <section id="demo" className="section-full bg-white">
        <div className="max-w-4xl mx-auto w-full">
          <div className="text-center mb-12 observe-me">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text mb-4">
              ¿Aún no tiene cuenta? Solicite acceso
            </h2>
            <p className="text-text-muted max-w-xl mx-auto text-lg">
              Rellene el formulario y nuestro equipo comercial se pondrá en contacto con usted
              en un plazo máximo de 24 horas laborables.
            </p>
          </div>

          <div className="observe-me">
            {requestSent ? (
              <div className="success-message text-center py-16 bg-green-50 rounded-2xl border border-green-100">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-success" />
                </div>
                <h3 className="text-xl font-bold text-text mb-2">Solicitud enviada correctamente</h3>
                <p className="text-text-muted max-w-md mx-auto">
                  Hemos recibido su solicitud. Nuestro equipo comercial se pondrá en contacto con usted
                  a la mayor brevedad posible.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleRequestAccess}
                className="bg-gray-50 rounded-2xl p-8 md:p-12 border border-gray-100"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-text mb-1.5">
                      Nombre del centro <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      value={requestForm.centro}
                      onChange={(e) => setRequestForm({ ...requestForm, centro: e.target.value })}
                      className={`request-input ${requestErrors.centro ? 'border-danger' : ''}`}
                      placeholder="Ej: Clínica San Rafael"
                    />
                    {requestErrors.centro && (
                      <span className="text-danger text-xs mt-1 block">{requestErrors.centro}</span>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text mb-1.5">
                      Persona de contacto <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      value={requestForm.contacto}
                      onChange={(e) => setRequestForm({ ...requestForm, contacto: e.target.value })}
                      className={`request-input ${requestErrors.contacto ? 'border-danger' : ''}`}
                      placeholder="Nombre y apellidos"
                    />
                    {requestErrors.contacto && (
                      <span className="text-danger text-xs mt-1 block">{requestErrors.contacto}</span>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text mb-1.5">
                      Email <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      value={requestForm.email}
                      onChange={(e) => setRequestForm({ ...requestForm, email: e.target.value })}
                      className={`request-input ${requestErrors.email ? 'border-danger' : ''}`}
                      placeholder="email@sucentro.com"
                    />
                    {requestErrors.email && (
                      <span className="text-danger text-xs mt-1 block">{requestErrors.email}</span>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text mb-1.5">
                      Teléfono <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      value={requestForm.telefono}
                      onChange={(e) => setRequestForm({ ...requestForm, telefono: e.target.value })}
                      className={`request-input ${requestErrors.telefono ? 'border-danger' : ''}`}
                      placeholder="954 000 000"
                    />
                    {requestErrors.telefono && (
                      <span className="text-danger text-xs mt-1 block">{requestErrors.telefono}</span>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-text mb-1.5">
                      Mensaje <span className="text-text-muted font-normal">(opcional)</span>
                    </label>
                    <textarea
                      value={requestForm.mensaje}
                      onChange={(e) => setRequestForm({ ...requestForm, mensaje: e.target.value })}
                      className="request-input"
                      placeholder="Indíquenos cualquier información adicional sobre su centro o necesidades..."
                      rows={4}
                    />
                  </div>
                </div>

                <div className="mt-8 text-center">
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 bg-secondary hover:bg-secondary-dark text-white font-semibold px-8 py-3.5 rounded-xl transition text-base"
                  >
                    <Send size={18} />
                    Enviar Solicitud
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ─── FAQ SECTION (full viewport, image + accordion) ─── */}
      <section id="faq" className="section-full bg-background">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Image */}
            <div className="observe-me slide-left hidden lg:block">
              <img
                src={IMAGES.faq}
                alt="Atención al cliente"
                className="rounded-2xl shadow-xl w-full object-cover"
                style={{ maxHeight: '600px' }}
                loading="lazy"
              />
            </div>

            {/* Right: FAQ content */}
            <div>
              <div className="mb-10 observe-me">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text mb-4">Preguntas frecuentes</h2>
                <p className="text-text-muted text-lg">
                  Respuestas a las dudas más habituales de nuestros clientes
                </p>
              </div>

              <div className="space-y-3 observe-me">
                {faqs.map((faq, i) => (
                  <div key={i} className={`faq-item bg-white ${openFaq === i ? 'open' : ''}`}>
                    <button
                      className="faq-question"
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    >
                      <span>{faq.q}</span>
                      <ChevronDown size={20} className="faq-chevron ml-4" />
                    </button>
                    <div className="faq-answer">
                      <p>{faq.a}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 observe-me">
                <p className="text-text-muted text-sm">
                  ¿Tiene más preguntas? Contáctenos en{' '}
                  <a href="mailto:info@hispafarma.com" className="text-primary font-medium hover:underline">
                    info@hispafarma.com
                  </a>
                  {' '}o llame al{' '}
                  <a href="tel:954353617" className="text-primary font-medium hover:underline">
                    954 353 617
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="bg-gray-900 text-white pt-16 pb-8 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            {/* Col 1: Logo */}
            <div className="lg:col-span-1">
              <div className="flex items-center mb-4">
                <img src="/logo.png" alt="ComprasSanitarias" className="h-16 w-auto" />
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Plataforma de gestión de compras sanitarias de Hispana de Parafarmacia S.L.
                Distribución de productos sanitarios y parafarmacia a nivel nacional.
              </p>
            </div>

            {/* Col 2: Links */}
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider text-gray-300 mb-4">
                Enlaces rápidos
              </h4>
              <div className="space-y-2.5">
                <button onClick={() => scrollTo('hero')} className="footer-link bg-transparent border-none cursor-pointer text-left">
                  Inicio
                </button>
                <button onClick={() => scrollTo('proceso')} className="footer-link bg-transparent border-none cursor-pointer text-left">
                  Cómo funciona
                </button>
                <button onClick={() => scrollTo('servicios')} className="footer-link bg-transparent border-none cursor-pointer text-left">
                  Servicios
                </button>
                <button onClick={() => scrollTo('login')} className="footer-link bg-transparent border-none cursor-pointer text-left">
                  Acceso clientes
                </button>
                <button onClick={() => scrollTo('faq')} className="footer-link bg-transparent border-none cursor-pointer text-left">
                  Preguntas frecuentes
                </button>
              </div>
            </div>

            {/* Col 3: Contact */}
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider text-gray-300 mb-4">
                Contacto
              </h4>
              <div className="space-y-3">
                <div className="flex items-start gap-2.5 text-gray-400 text-sm">
                  <MapPin size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                  <span>C/ Laton (Pol. Ind. Calonge), 1<br />41007 Sevilla</span>
                </div>
                <a href="tel:954353617" className="flex items-center gap-2.5 text-gray-400 text-sm hover:text-primary transition">
                  <Phone size={16} className="text-gray-500 flex-shrink-0" />
                  954 353 617
                </a>
                <a href="mailto:info@hispafarma.com" className="flex items-center gap-2.5 text-gray-400 text-sm hover:text-primary transition">
                  <Mail size={16} className="text-gray-500 flex-shrink-0" />
                  info@hispafarma.com
                </a>
              </div>
            </div>

            {/* Col 4: Legal */}
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider text-gray-300 mb-4">
                Legal
              </h4>
              <div className="space-y-2.5">
                <span className="text-gray-400 text-sm block">CIF: B91324665</span>
                <a href="#" className="footer-link">Aviso Legal</a>
                <a href="#" className="footer-link">Política de Privacidad</a>
                <a href="#" className="footer-link">Política de Cookies</a>
                <a href="#" className="footer-link">Condiciones de Venta</a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="text-gray-500 text-xs">
              &copy; {new Date().getFullYear()} Hispana de Parafarmacia S.L. Todos los derechos reservados.
            </p>
            <p className="text-gray-600 text-xs">
              Plataforma ComprasSanitarias v2.0
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
